import React, { FC, useCallback, useEffect, useRef, useState } from "react";

import { Box, Fade, Paper } from "@mui/material";

import { Status, SubmissionInfo } from "../types";
import { InformationDisplayer } from "../InformationDisplayer/InformationDisplayer";

import "./SubmissionInformation.css";

export type SubmissionInformationProps = {
  info: SubmissionInfo;
  cbOnEndOfAnimation?: () => void;
  displayTime: number;
};

export const SubmissionInformation: FC<SubmissionInformationProps> = ({
  info,
  cbOnEndOfAnimation,
  displayTime,
}) => {
  const { status } = info;
  const [visible, setVisible] = useState(true);

  useTimeout(
    () => {
      if (status === Status.success) {
        setVisible(false);
      }
    },
    status === Status.success ? displayTime : null
  );

  const onActionClick = () => {
    setVisible(false);
  };

  return (
    <>
      <Box
        style={{
          margin: "0 auto",
        }}
      >
        <Fade
          in={visible}
          unmountOnExit
          onExited={() => {
            cbOnEndOfAnimation?.();
          }}
        >
          <Box>
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Paper
                elevation={3}
                style={{
                  padding: `0 ${
                    status === Status.success ? "56" : "8"
                  }px 0 8px`,
                }}
              >
                <InformationDisplayer
                  info={info}
                  action={
                    status === Status.error
                      ? { text: "Close", clickHandler: onActionClick }
                      : undefined
                  }
                />
              </Paper>
            </Box>
          </Box>
        </Fade>
      </Box>
    </>
  );
};

const useTimeout = (callback: () => void, delay: number | null) => {
  const timeoutRef = useRef<NodeJS.Timeout>();
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const set = useCallback(() => {
    timeoutRef.current && clearTimeout(timeoutRef.current);

    if (delay !== null) {
      timeoutRef.current = setTimeout(() => callbackRef.current(), delay);
    }
  }, [delay]);

  const clear = useCallback(() => {
    timeoutRef.current && clearTimeout(timeoutRef.current);
  }, []);

  const reset = useCallback(() => {
    clear();
    set();
  }, [clear, set]);

  useEffect(() => {
    set();
    return clear;
  }, [delay, set, clear]);

  return { reset, clear };
};
